/**
 * The editor's one store, and its one way to change anything.
 *
 * PRD §3.4, verbatim: "undo/redo is an Immer patch stack. Every mutation —
 * human or AI — is a patch, so both undo through one mechanism." RULE-018 adds
 * the failure it prevents: "A direct `setState` in the editor silently breaks
 * undo."
 *
 * SILENTLY is the word that decides this file's shape. A bypassed mutation does
 * not throw, does not warn and does not look wrong — it works perfectly until
 * somebody presses undo, and then it either skips their last edit or reverts
 * something they did three edits ago. By the time that is noticed the cause is
 * long gone from the screen. So the discipline cannot rest on remembering it.
 *
 * The mechanism is the one `packages/api/src/db/client.ts` uses for the tenant
 * boundary, for the same reason: **there is no `export const store`.** Zustand's
 * `setState` lives on the store object, so keeping the object module-private
 * means the bypass is not merely discouraged, it is unreachable — a caller
 * cannot write the broken line because the thing it would be written on does
 * not leave this file. `tests/editor-store-discipline.test.ts` holds the door
 * shut against a future export.
 *
 * The AI half of "human or AI" is why `edit()` takes a recipe rather than a
 * finished funnel. At M3 the one-shot author produces a funnel; if it replaced
 * state wholesale there would be one patch covering everything and undo would
 * be all-or-nothing. A recipe produces the same fine-grained patches a person's
 * edit does, so M3 needs no second mechanism — which is the whole of §3.4.
 *
 * Story 6.2.
 */
import { create } from 'zustand'
import { applyPatches, enablePatches, produceWithPatches, type Patch } from 'immer'
import type { Funnel } from '@leadgible/schema'

// Immer does not record patches unless asked. Called once, at module load,
// before any `produceWithPatches` can run.
enablePatches()

/** One undoable step: the patches that make it, and the ones that unmake it. */
export type Step = {
  /** Human-readable, for the day an undo history is drawn (nothing draws one). */
  readonly label: string
  readonly redo: readonly Patch[]
  readonly undo: readonly Patch[]
}

/** How the draft stands against the server. */
export type SaveState =
  | { status: 'clean' }
  | { status: 'dirty' }
  | { status: 'saving' }
  | { status: 'failed'; message: string }

export type EditorState = {
  /** Null before the first `load`, and while a load is in flight. */
  funnel: Funnel | null
  /** The funnel id the editor is open on, for the save call. */
  funnelId: string | null
  past: Step[]
  future: Step[]
  save: SaveState
}

const EMPTY: EditorState = {
  funnel: null,
  funnelId: null,
  past: [],
  future: [],
  save: { status: 'clean' },
}

/**
 * PRIVATE. Never exported, never returned, never attached to anything that is.
 *
 * Everything below closes over it. That is the only reference in the program.
 */
const store = create<EditorState>()(() => EMPTY)

/** How many steps the stack keeps. Deep enough to cover a working session. */
export const HISTORY_LIMIT = 200

/* ── reads ──────────────────────────────────────────────────────────────── */

/**
 * The subscribing read. `useEditor((s) => s.funnel?.steps)` re-renders only when
 * that selection changes, which is what keeps six tabs over one document from
 * re-rendering all of it on every keystroke.
 *
 * A FUNCTION WRAPPING THE STORE, not the store itself. Zustand's hook and its
 * store are the same object — `useEditor.setState` would exist and be writable
 * if this were `export const useEditor = store`, and every word in this file's
 * header about the bypass being unreachable would be false. The wrapper is the
 * whole guarantee.
 */
export function useEditor<T>(selector: (state: EditorState) => T): T {
  return store(selector)
}

/** A non-reactive read, for event handlers and the save loop. */
export const editorState = (): EditorState => store.getState()

export const canUndo = (s: EditorState): boolean => s.past.length > 0
export const canRedo = (s: EditorState): boolean => s.future.length > 0

/* ── writes — all four of them ──────────────────────────────────────────── */

/**
 * Open a funnel. Resets the history: undoing past the moment a document was
 * opened would restore a shape the server never had.
 */
export function load(funnelId: string, funnel: Funnel): void {
  store.setState({ ...EMPTY, funnelId, funnel })
}

/**
 * THE mutator. Every change to the draft goes through here.
 *
 * A recipe that changes nothing produces no patches and is not recorded — an
 * undo step that undoes nothing is worse than no step, because pressing undo
 * would appear to do nothing at all.
 *
 * `coalesce` merges this change into the previous step when the two carry the
 * same label. Typing is the reason: a text field calls `edit` on every
 * keystroke, so without it a fifty-character funnel name is fifty undo steps —
 * a quarter of the whole history spent on one field, and fifty presses of
 * Ctrl+Z to get back to where the person started. Merging is exact rather than
 * approximate: the combined step redoes both changes in order and undoes them
 * in reverse, which is what makes it a single step and not a lossy one.
 */
export function edit(
  label: string,
  recipe: (draft: Funnel) => void,
  options: { coalesce?: boolean } = {},
): void {
  const state = store.getState()
  if (!state.funnel) return

  const [next, redo, undo] = produceWithPatches(state.funnel, recipe)
  if (redo.length === 0) return

  const previous = state.past[state.past.length - 1]
  const merge = options.coalesce === true && previous !== undefined && previous.label === label

  const past = merge
    ? [
        ...state.past.slice(0, -1),
        {
          label,
          redo: [...previous.redo, ...redo],
          // Reverse order: the later change is undone before the earlier one.
          undo: [...undo, ...previous.undo],
        },
      ]
    : [...state.past, { label, redo, undo }]

  store.setState({
    funnel: next,
    // Oldest first out. A stack that grows without limit is a tab that gets
    // slower the longer somebody works in it.
    past: past.length > HISTORY_LIMIT ? past.slice(past.length - HISTORY_LIMIT) : past,
    // A new edit after undoing abandons the redo branch, as every editor does.
    future: [],
    save: { status: 'dirty' },
  })
}

export function undo(): void {
  const state = store.getState()
  const step = state.past[state.past.length - 1]
  if (!step || !state.funnel) return

  store.setState({
    funnel: applyPatches(state.funnel, step.undo as Patch[]),
    past: state.past.slice(0, -1),
    future: [step, ...state.future],
    save: { status: 'dirty' },
  })
}

export function redo(): void {
  const state = store.getState()
  const [step, ...rest] = state.future
  if (!step || !state.funnel) return

  store.setState({
    funnel: applyPatches(state.funnel, step.redo as Patch[]),
    past: [...state.past, step],
    future: rest,
    save: { status: 'dirty' },
  })
}

/**
 * Where the save loop reports to.
 *
 * Kept apart from `edit` deliberately: saving is not an edit and must not push
 * a patch. If it did, undo would step through save results instead of through
 * the person's own changes.
 */
export function setSaveState(save: SaveState): void {
  store.setState({ save })
}

/** Leaving the editor. Nothing about one funnel outlives the tab that showed it. */
export function reset(): void {
  store.setState(EMPTY)
}
